import { useMemo } from "react";
import { useSelector } from "react-redux";
import { LICENSE_SKU_PATTERNS, LICENSE_STATUS } from "../../../global/constants/licensesConstants";
import { DEVICE_CONSTANTS } from "../../../constants/DeviceConstants";

/**
 * Shared hook for HS Data Collection "Enable Search" state.
 *
 * @param {boolean} editFlow - true when in edit-camera modal (offline/calibration guards apply)
 * @param {string}  cameraId - the camera's device ID
 * @param {Array}   enabledItems - array of FOV/stream objects that have an `enableSearch` boolean
 * @returns {{ isCameraOffline: boolean, effectiveAvailable: number|null, notCalibratedFovIds: Set<string>, calibratingFovIds: Set<string> }}
 */
export const useEnableSearchState = ({ editFlow, cameraId, enabledItems }) => {
  const cameraList = useSelector((state) => state.deviceReducer.cameraList);
  const fovList = useSelector((state) => state.deviceReducer.fovList);
  const licenses = useSelector((state) => state.licenseReducer.licenses);

  const liveCamera = editFlow ? cameraList?.find((c) => c.id === cameraId) : null;
  const isCameraOffline =
    editFlow &&
    (liveCamera?.fields?.connectivityStatus || liveCamera?.connectivityStatus || "").toLowerCase() ===
      DEVICE_CONSTANTS.CAMERA_OFFLINE_STATUS;

  const searchLicense = licenses?.find((l) => l.sku === LICENSE_SKU_PATTERNS.FORENSIC_SEARCH_V2);

  const currentEnabledCount = (enabledItems || []).filter((item) => !!item.enableSearch).length;

  const serverEnabledCount = useMemo(() => {
    if (!editFlow || !cameraId) return 0;
    return (
      fovList?.filter((f) => {
        if (f.parentdeviceid !== cameraId) return false;
        const hsInfo = f.licensesinfo?.find((l) => l.sku === LICENSE_SKU_PATTERNS.FORENSIC_SEARCH_V2);
        return hsInfo?.status?.toLowerCase() === LICENSE_STATUS.ACTIVE;
      })?.length ?? 0
    );
  }, [fovList, cameraId, editFlow]);

  const notCalibratedFovIds = useMemo(() => {
    if (!editFlow || !cameraId) return new Set();
    return new Set(
      (fovList || [])
        .filter((f) => {
          if (f.parentdeviceid !== cameraId) return false;
          const calStatus = f.fields?.calibration?.status;
          return calStatus === undefined || calStatus < 0;
        })
        .map((f) => f.id)
    );
  }, [fovList, cameraId, editFlow]);

  const calibratingFovIds = useMemo(() => {
    if (!editFlow || !cameraId) return new Set();
    return new Set(
      (fovList || [])
        .filter((f) => {
          if (f.parentdeviceid !== cameraId) return false;
          const calStatus = f.fields?.calibration?.status;
          return (
            f.state?.toLowerCase() === DEVICE_CONSTANTS.CAMERA_STATUS_REGISTERED.toLowerCase() &&
            calStatus >= 0 &&
            calStatus < 100
          );
        })
        .map((f) => f.id)
    );
  }, [fovList, cameraId, editFlow]);

  const effectiveAvailable = searchLicense
    ? Math.max(0, (searchLicense.availablelicenses ?? 0) - (currentEnabledCount - serverEnabledCount))
    : null;

  return { isCameraOffline, effectiveAvailable, notCalibratedFovIds, calibratingFovIds };
};
